/**
 * Language Context Provider
 *
 * Provides UI language translation functionality throughout the application.
 * Uses the user's uiLanguage preference from Firestore, defaulting to Ukrainian.
 */

import React, { createContext, useContext, useMemo, useCallback, ReactNode } from 'react';
import { useAuth } from '../hooks/useAuth';
import { updateUserProfile } from '../services/firebase/auth';
import {
  Language,
  DEFAULT_LANGUAGE,
  createTranslator,
  LANGUAGE_OPTIONS,
} from '../locales';

/**
 * Language context value type
 */
export interface LanguageContextValue {
  /** Current UI language */
  language: Language;
  /** Translation function - use t('key.path') to get translated string */
  t: (key: string) => string;
  /** Change the UI language */
  setLanguage: (language: Language) => Promise<void>;
  /** Available language options */
  languageOptions: typeof LANGUAGE_OPTIONS;
  /** Whether language is being saved */
  isChangingLanguage: boolean;
}

/**
 * Create the language context
 */
export const LanguageContext = createContext<LanguageContextValue>({
  language: DEFAULT_LANGUAGE,
  t: (key: string) => key,
  setLanguage: async () => {},
  languageOptions: LANGUAGE_OPTIONS,
  isChangingLanguage: false,
});

/**
 * Props for LanguageProvider component
 */
interface LanguageProviderProps {
  children: ReactNode;
}

/**
 * Language Provider Component
 *
 * Wraps the application and provides translation functionality to all children.
 * Reads language preference from userDocument.uiLanguage, defaults to Ukrainian.
 */
export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { user, userDocument } = useAuth();
  const [isChangingLanguage, setIsChangingLanguage] = React.useState(false);

  // Get current language from user document or default to Ukrainian
  const language: Language = useMemo(() => {
    const uiLang = userDocument?.uiLanguage;
    if (uiLang === 'en' || uiLang === 'uk') {
      return uiLang;
    }
    return DEFAULT_LANGUAGE;
  }, [userDocument?.uiLanguage]);

  // Create memoized translator function
  const t = useMemo(() => createTranslator(language), [language]);

  // Handle language change
  const setLanguage = useCallback(async (newLanguage: Language) => {
    if (!user?.uid || newLanguage === language) return;

    setIsChangingLanguage(true);
    try {
      await updateUserProfile(user.uid, { uiLanguage: newLanguage });
    } catch (error) {
      console.error('Error updating UI language:', error);
    } finally {
      setIsChangingLanguage(false);
    }
  }, [user?.uid, language]);

  const value = useMemo(
    () => ({
      language,
      t,
      setLanguage,
      languageOptions: LANGUAGE_OPTIONS,
      isChangingLanguage,
    }),
    [language, t, setLanguage, isChangingLanguage]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

/**
 * Hook to use language context
 */
export function useLanguage(): LanguageContextValue {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export default LanguageProvider;
